import v2 from '../v2Client';
import { isAwaitingConfirmation, isTicketFinished } from '../../utils/ticketStates';
import { readApiErrorMessage } from '../../utils/apiErrorMessages';
import { backofficeTokenHeader } from '../../gateways/PackagingCatalogGateway';
import { MATERIALS_ORDER_TOPIC, toItemFields } from '../../components/Tickets/materialsOrderSelection';

const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data' };

function buildTicketFormData(payload) {
  const formData = new FormData();
  formData.append('linea_negocio', payload.linea_negocio);
  formData.append('topic', payload.topic);
  formData.append('priority', payload.priority);
  formData.append('asunto', payload.asunto);
  formData.append('descripcion', payload.descripcion);
  (payload.adjuntos || []).forEach((file, i) => {
    formData.append(`adjuntos[${i}]`, file);
  });
  if (payload.topic === MATERIALS_ORDER_TOPIC) {
    toItemFields(payload.items || {}).forEach(([field, value]) => formData.append(field, value));
  }
  return formData;
}

function ticketRequestHeaders(payload) {
  return payload.topic === MATERIALS_ORDER_TOPIC
    ? { ...MULTIPART_HEADERS, ...backofficeTokenHeader() }
    : MULTIPART_HEADERS;
}

const state = {
  configuracion: null,
  cargandoConfiguracion: false,
  creandoTicket: false,
  ticketRecienCreado: null,
  error: null,
  misTickets: [],
  cargandoMisTickets: false,
  errorMisTickets: null,
};

const mutations = {
  SET_CONFIGURACION(state, payload) {
    state.configuracion = payload;
  },
  SET_CARGANDO_CONFIG(state, value) {
    state.cargandoConfiguracion = value;
  },
  SET_CREANDO(state, value) {
    state.creandoTicket = value;
  },
  SET_TICKET_CREADO(state, payload) {
    state.ticketRecienCreado = payload;
  },
  SET_ERROR(state, message) {
    state.error = message;
  },
  RESET(state) {
    state.creandoTicket = false;
    state.ticketRecienCreado = null;
    state.error = null;
  },
  SET_MIS_TICKETS(state, payload) {
    state.misTickets = payload;
  },
  SET_CARGANDO_MIS_TICKETS(state, value) {
    state.cargandoMisTickets = value;
  },
  SET_ERROR_MIS_TICKETS(state, message) {
    state.errorMisTickets = message;
  },
};

const actions = {
  async cargarConfiguracion({ commit, state }, { force = false } = {}) {
    if (state.configuracion && !force) return;
    commit('SET_CARGANDO_CONFIG', true);
    try {
      const { data } = await v2.get('/tickets/configuracion');
      commit('SET_CONFIGURACION', data.data);
    } catch (e) {
      commit('SET_ERROR', 'No se pudo cargar la configuración de tickets');
    } finally {
      commit('SET_CARGANDO_CONFIG', false);
    }
  },

  async crearTicket({ commit }, payload) {
    commit('SET_CREANDO', true);
    commit('SET_ERROR', null);

    try {
      const { data } = await v2.post('/tickets', buildTicketFormData(payload), {
        headers: ticketRequestHeaders(payload),
      });
      commit('SET_TICKET_CREADO', data.data);
      return data.data;
    } catch (e) {
      const msg = readApiErrorMessage(e, 'Error al crear ticket');
      commit('SET_ERROR', msg);
      throw e;
    } finally {
      commit('SET_CREANDO', false);
    }
  },

  resetTicket({ commit }) {
    commit('RESET');
  },

  async confirmResolution({ dispatch }, { ticketId }) {
    await v2.patch(`/tickets/${encodeURIComponent(ticketId)}/confirmar`);
    await dispatch('cargarMisTickets');
  },

  async rejectResolution({ dispatch }, { ticketId, reason }) {
    await v2.patch(`/tickets/${encodeURIComponent(ticketId)}/rechazar`, { reason });
    await dispatch('cargarMisTickets');
  },

  async cargarMisTickets({ commit }) {
    commit('SET_CARGANDO_MIS_TICKETS', true);
    commit('SET_ERROR_MIS_TICKETS', null);
    try {
      const { data } = await v2.get('/tickets/mis-tickets');
      commit('SET_MIS_TICKETS', data.data);
    } catch (e) {
      commit('SET_ERROR_MIS_TICKETS', 'No se pudieron cargar tus tickets');
    } finally {
      commit('SET_CARGANDO_MIS_TICKETS', false);
    }
  },
};

const getters = {
  configuracionTickets: (state) => state.configuracion,
  ticketsCargandoConfig: (state) => state.cargandoConfiguracion,
  ticketsCreando: (state) => state.creandoTicket,
  ticketRecienCreado: (state) => state.ticketRecienCreado,
  ticketsError: (state) => state.error,
  misTickets: (state) => state.misTickets,
  misTicketsCargando: (state) => state.cargandoMisTickets,
  misTicketsError: (state) => state.errorMisTickets,
  misTicketsActivos: (state) => state.misTickets
    .filter((t) => !isTicketFinished(t.estado))
    .sort((a, b) => isAwaitingConfirmation(b.estado) - isAwaitingConfirmation(a.estado)),
  misTicketsResueltos: (state) => state.misTickets.filter((t) => isTicketFinished(t.estado)),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
